import { AppImage as Image } from "@/shared/ui/AppImage";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/shared/lib/cn";
import { accountRoutes, getStoredAccountType } from "@/modules/safecheck/auth";

type SafecheckLogoProps = {
  className?: string;
  size?: "sm" | "md" | "lg";
  /** Logo clair sur fond marine (sidebar, panneau login) */
  variant?: "default" | "onDark";
  /** Si false, logo non cliquable (ex. page login) */
  linkToDashboard?: boolean;
  /** Bouclier seul, sans le texte (menu latéral replié) */
  markOnly?: boolean;
};

const sizeClasses = {
  sm: "h-12",
  md: "h-16",
  lg: "h-20",
} as const;

export function SafecheckLogo({
  className,
  size = "md",
  variant = "default",
  linkToDashboard = true,
  markOnly = false,
}: SafecheckLogoProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [dashboardHref, setDashboardHref] = useState(accountRoutes.enterprise);

  useEffect(() => {
    setMounted(true);
    const accountType = getStoredAccountType();
    if (accountType) {
      setDashboardHref(accountRoutes[accountType]);
    }
  }, []);

  const imageClass = cn("w-auto", sizeClasses[size]);
  const logoSrc =
    variant === "onDark" || (mounted && resolvedTheme === "dark")
      ? "/logo-2.png"
      : "/logo-1.png";

  const logo = markOnly ? (
    <Image src="/bouclier.png" alt="Safecheck RDC" width={470} height={494} className="h-11 w-auto" priority />
  ) : (
    <Image
      src={logoSrc}
      alt="Safecheck RDC"
      width={280}
      height={74}
      className={imageClass}
      priority
    />
  );

  if (!linkToDashboard) {
    return <div className={cn("flex items-center", className)}>{logo}</div>;
  }

  return (
    <Link
      to={dashboardHref}
      className={cn("flex items-center transition-opacity hover:opacity-90", className)}
      aria-label="Tableau de bord"
    >
      {logo}
    </Link>
  );
}
